import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileText } from 'lucide-react';
import Button from '../../../components/Button';
import { formatDateOrNA } from '../../../utils/dateUtils';
import type { DocumentCategory, SubmittedFileEntry } from '../../../types';
import { resolveDocumentTypeLabel } from '../../../utils/documentTypeMapper';
import { SUBTYPE_CATEGORY_MAP } from '../../../utils/documentTypeRef';
import { logger } from '../../../utils/logger';

/**
 * Display order for the Documents tab. The void cheque sits in the
 * second-to-last position (specimen avant-dernier — voulu) — typically a single
 * entry, after invoices and supporting documents which are the bulk of the list.
 */
const CATEGORY_ORDER: DocumentCategory[] = [
  'INVOICE',
  'SUPPORTING_DOCUMENT',
  'VOID_CHEQUE',
  'OTHER',
];

/**
 * Subtypes considered "the generic catch-all" for their parent category. These get
 * promoted to the top of the bucket, alongside the UNKNOWN bucket, before the
 * specific named subtypes. DEV-6974 design — users expect the umbrella "Invoice"
 * label to lead the list, not be buried under the more specific bills.
 */
const GENERIC_SUBTYPES_BY_CATEGORY: Record<DocumentCategory, string[]> = {
  // NonMedicalBill resolves to documentTypes.INVOICE.shortLabel — the bare "Invoice".
  INVOICE: ['NonMedicalBill'],
  // TripCancellationProof + BaggageProof both resolve to documentTypes.PROOF.shortLabel
  // ("Preuve" / "Proof") — the bare unqualified supporting-document label. Listing
  // TripCancellationProof is enough; BaggageProof rides along on the shared shortKey.
  SUPPORTING_DOCUMENT: ['TripCancellationProof'],
  // CheckSample is the only subtype, so it's "the" void-cheque entry by default.
  VOID_CHEQUE: ['CheckSample'],
  OTHER: [],
};

/**
 * Per-category bucket order, expressed as **resolved shortKeys** rather than raw
 * subtype names. Two DEV-6164 subtypes that resolve to the same shortKey (e.g.
 * {@code TripCancellationProof} + {@code BaggageProof} → {@code PROOF.shortLabel})
 * MUST land in the same visual bucket — grouping by raw type would render two
 * consecutive identical "Proof" sub-headings. Order layers as:
 *   1. Generic shortKeys (from {@link GENERIC_SUBTYPES_BY_CATEGORY}).
 *   2. The UNKNOWN bucket.
 *   3. Specific shortKeys in canonical `SUBTYPE_CATEGORY_MAP` declaration order
 *      (deduped — first appearance wins).
 */
const SUBTYPE_SHORTKEY_ORDER_BY_CATEGORY: Record<DocumentCategory, string[]> = (() => {
  const order: Record<DocumentCategory, string[]> = {
    INVOICE: [],
    SUPPORTING_DOCUMENT: [],
    VOID_CHEQUE: [],
    OTHER: [],
  };
  // First pass: generics by shortKey.
  for (const category of Object.keys(order) as DocumentCategory[]) {
    for (const subtype of GENERIC_SUBTYPES_BY_CATEGORY[category]) {
      const sk = resolveDocumentTypeLabel(subtype).shortKey;
      if (!order[category].includes(sk)) order[category].push(sk);
    }
  }
  // Second pass: specifics by canonical declaration order, deduped by shortKey.
  for (const [subtype, category] of Object.entries(SUBTYPE_CATEGORY_MAP)) {
    const sk = resolveDocumentTypeLabel(subtype).shortKey;
    if (!order[category].includes(sk)) order[category].push(sk);
  }
  return order;
})();

/** ShortKeys considered generic in each category — used to compute insertion of UNKNOWN. */
const GENERIC_SHORTKEYS_BY_CATEGORY: Record<DocumentCategory, string[]> = {
  INVOICE: GENERIC_SUBTYPES_BY_CATEGORY.INVOICE.map((s) => resolveDocumentTypeLabel(s).shortKey),
  SUPPORTING_DOCUMENT: GENERIC_SUBTYPES_BY_CATEGORY.SUPPORTING_DOCUMENT.map((s) => resolveDocumentTypeLabel(s).shortKey),
  VOID_CHEQUE: GENERIC_SUBTYPES_BY_CATEGORY.VOID_CHEQUE.map((s) => resolveDocumentTypeLabel(s).shortKey),
  OTHER: GENERIC_SUBTYPES_BY_CATEGORY.OTHER.map((s) => resolveDocumentTypeLabel(s).shortKey),
};

/** Bucket key for unknown / unmapped subtypes inside the sub-grouping pass. */
const UNKNOWN_SUBTYPE_KEY = '__UNKNOWN__';

export interface ClaimDocumentsTabProps {
  documentsList: SubmittedFileEntry[];
  language: string;
  onDownload: (doc: SubmittedFileEntry) => void;
}

interface DocumentRowProps {
  doc: SubmittedFileEntry;
  language: string;
  onDownload: () => void;
}

const DocumentRow: React.FC<DocumentRowProps> = ({ doc, language, onDownload }) => {
  const { t } = useTranslation();

  // Defensive split: BFF emits ISO 8601 with a `T` separator; legacy drafts may
  // already have a date-only string. Either way `formatDateOrNA` handles `null`.
  const datePart = doc.uploadedAt ? doc.uploadedAt.split('T')[0] : null;
  const formattedDate = formatDateOrNA(datePart, language === 'fr' ? 'fr-CA' : 'en-CA');

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gray-50 p-4 rounded-lg">
      <div className="flex items-start sm:items-center space-x-3 min-w-0 flex-1">
        <FileText className="w-6 h-6 text-tenant-600 flex-shrink-0 mt-0.5 sm:mt-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 break-all">{doc.name}</p>
          <p className="text-xs text-gray-600">
            {t('claimDetails.documents.uploadedOn')} {formattedDate}
          </p>
        </div>
      </div>
      <Button
        variant="secondary"
        size="sm"
        onClick={onDownload}
        className="self-end sm:self-center flex-shrink-0"
      >
        <Download className="w-4 h-4 mr-2" />
        {t('claimDetails.documents.download')}
      </Button>
    </div>
  );
};

/**
 * DEV-6896 / DEV-6974 — Documents tab content for `ClaimDetailsScreen`.
 *
 * Groups documents by {@link DocumentCategory}, then by DEV-6164 PascalCase
 * subtype within each category. Empty groups are hidden so the user only sees
 * sections that actually contain files. Category order is fixed
 * (`INVOICE → SUPPORTING_DOCUMENT → VOID_CHEQUE → OTHER`); sub-headings follow
 * the canonical declaration order from `SUBTYPE_CATEGORY_MAP` and the unknown
 * bucket sits at the end of its category.
 *
 * When the BFF surfaces a `doc.type` that isn't recognised by
 * {@link resolveDocumentTypeLabel} (e.g. NOVA drifted ahead of the FE catalog),
 * the row renders the generic `documentTypes.UNKNOWN.shortLabel` ("Document")
 * and the raw NOVA value is logged as a warn for diagnostics — never leaked
 * to the UI as a raw PascalCase string.
 */
const ClaimDocumentsTab: React.FC<ClaimDocumentsTabProps> = ({
  documentsList,
  language,
  onDownload,
}) => {
  const { t } = useTranslation();

  // Warn-log each unique unmapped subtype on render. Helps diagnose drift between
  // NOVA `Submitted_File.File_Type` values and the FE catalog (`DOCMANA_TO_PORTAL`).
  useEffect(() => {
    const unknownTypes = new Set<string>();
    for (const doc of documentsList) {
      if (!resolveDocumentTypeLabel(doc.type).isKnown) {
        unknownTypes.add(doc.type ?? '<null>');
      }
    }
    for (const documentType of unknownTypes) {
      logger.warn('Unknown document subtype on claim documents tab', { documentType });
    }
  }, [documentsList]);

  const grouped = useMemo(() => {
    // First pass: bucket by category → bucketKey → docs. The bucketKey is either
    // the resolved shortKey (so two raw subtypes sharing a shortKey end up in the
    // same visual bucket — e.g. TripCancellationProof + BaggageProof both → PROOF)
    // or the UNKNOWN sentinel when the raw type can't be mapped at all.
    const byCategoryThenBucket = new Map<DocumentCategory, Map<string, SubmittedFileEntry[]>>();
    for (const doc of documentsList) {
      const resolved = resolveDocumentTypeLabel(doc.type);
      const bucketKey = resolved.isKnown ? resolved.shortKey : UNKNOWN_SUBTYPE_KEY;
      const subMap =
        byCategoryThenBucket.get(doc.category) ?? new Map<string, SubmittedFileEntry[]>();
      const list = subMap.get(bucketKey) ?? [];
      list.push(doc);
      subMap.set(bucketKey, list);
      byCategoryThenBucket.set(doc.category, subMap);
    }

    // Second pass: split each category's buckets into:
    //   - `headlessDocs` — flat list of docs that should render with NO h4 (generic
    //     subtypes whose label would duplicate the category h3, plus UNKNOWN). Appear
    //     first, under the category h3 directly.
    //   - `headedSubgroups` — specific named subtypes that DO get an h4 sub-heading,
    //     in canonical declaration order.
    return CATEGORY_ORDER.flatMap((category) => {
      const subMap = byCategoryThenBucket.get(category);
      if (!subMap || subMap.size === 0) return [];

      const genericShortKeys = GENERIC_SHORTKEYS_BY_CATEGORY[category];
      const specificShortKeys = SUBTYPE_SHORTKEY_ORDER_BY_CATEGORY[category].filter(
        (sk) => !genericShortKeys.includes(sk),
      );

      const headlessDocs: SubmittedFileEntry[] = [];
      for (const sk of genericShortKeys) {
        headlessDocs.push(...(subMap.get(sk) ?? []));
      }
      headlessDocs.push(...(subMap.get(UNKNOWN_SUBTYPE_KEY) ?? []));

      const headedSubgroups = specificShortKeys
        .map((shortKey) => ({ shortKey, docs: subMap.get(shortKey) ?? [] }))
        .filter((sg) => sg.docs.length > 0);

      if (headlessDocs.length === 0 && headedSubgroups.length === 0) return [];
      return [{ category, headlessDocs, headedSubgroups }];
    });
  }, [documentsList]);

  if (documentsList.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        {t('claimDetails.documents.noDocuments')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {grouped.map(({ category, headlessDocs, headedSubgroups }) => (
        <section key={category}>
          <h3 className="text-base font-semibold text-gray-900 mb-3">
            {t(`claimDetails.documents.category.${category}`)}
          </h3>
          <div className="space-y-4">
            {headlessDocs.length > 0 && (
              <div className="space-y-3">
                {headlessDocs.map((doc) => (
                  <DocumentRow
                    key={doc.documentKey}
                    doc={doc}
                    language={language}
                    onDownload={() => onDownload(doc)}
                  />
                ))}
              </div>
            )}
            {headedSubgroups.map(({ shortKey, docs }) => (
              <div key={shortKey}>
                <h4 className="text-sm font-medium text-gray-700 mb-2">{t(shortKey)}</h4>
                <div className="space-y-3">
                  {docs.map((doc) => (
                    <DocumentRow
                      key={doc.documentKey}
                      doc={doc}
                      language={language}
                      onDownload={() => onDownload(doc)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default ClaimDocumentsTab;
